import { useState, useEffect, useMemo } from 'react';
import { useService } from '@aesop-fables/containr-react';
import { AvailablePricebooks, PricebookBundleMapping } from '../models/PricebookData';
import { AdminTierTypeEnum } from '../services/drillDown';
import { PricebookBundleApi } from '../api/apis/PricebookBundleApi';
import { ApiKeys } from '../api/apis/ApiKeys';
import { LicenseAgreementData } from '../api/apis/LicenseAgreementApi';
import { getLicensedBasePricebookIds } from '../data/rev-share/helpers/revShareSettingsUtils';

export interface OrgBundleMappingsResult {
  mappings: PricebookBundleMapping[];
  initialized: boolean;
  error: boolean;
}

export const useOrgBundleMappings = (
  adminTier: AdminTierTypeEnum | undefined,
  availablePricebooks: AvailablePricebooks | undefined,
  licenseAgreements: LicenseAgreementData[],
): OrgBundleMappingsResult => {
  const bundleApi = useService<PricebookBundleApi>(ApiKeys.PricebookBundle);
  const [mappings, setMappings] = useState<PricebookBundleMapping[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(false);

  const licensedBaseIds = useMemo(() => {
    if (adminTier !== AdminTierTypeEnum.Organization || !availablePricebooks) return [];

    return getLicensedBasePricebookIds(
      availablePricebooks.pricebookBundleDtos ?? [],
      licenseAgreements,
    ).sort((a, b) => a - b);
  }, [adminTier, availablePricebooks, licenseAgreements]);

  // Depend on a value-stable key instead of the array reference.
  // React compares deps by reference, so a new array instance would retrigger the effect.
  const licensedBaseIdsKey = licensedBaseIds.join(',');

  useEffect(() => {
    const ids = licensedBaseIdsKey ? licensedBaseIdsKey.split(',').map(Number) : [];

    if (ids.length === 0) {
      setMappings([]);
      setInitialized(true);
      setError(false);
      return;
    }

    setInitialized(false);
    setError(false);
    let isCancelled = false;

    Promise.all(ids.map(id => bundleApi.getByPrimaryPricebook(id)))
      .then(responses => {
        if (!isCancelled) {
          setMappings(responses.flatMap(response => response.data));
          setInitialized(true);
        }
      })
      .catch(err => {
        if (!isCancelled) {
          console.error('Error loading pricebook bundle mappings', err);
          setError(true);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [licensedBaseIdsKey, bundleApi]);

  return { mappings, initialized, error };
};
